import useAuth from '@app/auth/use-auth';
import Navbar from '@app/components/layout/nav/navbar';
import Page from '@app/components/layout/page/page';

import { ccfRoutes } from '@app/ccf/ccf-routes';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateOperationReport,
  useGetOperationReport,
  useUpdateOperationReport,
} from '@shared/api/hooks/reports/reports';
import {
  ContextMetricEnum,
  ContextMetricUnitEnum,
  OperationReportGoodsTypeEnum,
  OperationReportTypeEnum,
} from '@shared/api/types';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';
import { object, z } from 'zod';
import { NO_METRIC_OPTION } from '../options/metric-options';
import {
  getReportsPathMap,
  ReportFilters,
  useSortReportFilters,
} from '../utils';

const metricRequiresUnit = (
  metric: ContextMetricEnum | typeof NO_METRIC_OPTION
) => {
  return (
    metric === ContextMetricEnum.money_spent ||
    metric === ContextMetricEnum.money_earned
  );
};

const schema = object({
  title: z.string().min(1, 'Name is required'),
  timePeriods: z.array(z.string()).optional(),
  goodsTypes: z.array(z.nativeEnum(OperationReportGoodsTypeEnum)).optional(),
  sites: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  customers: z.array(z.string()).optional(),
  contextMetric: z
    .union([z.literal(NO_METRIC_OPTION), z.nativeEnum(ContextMetricEnum)])
    .default(NO_METRIC_OPTION),
  contextMetricValue: z.coerce
    .number({ invalid_type_error: 'Enter a number' })
    .min(1, 'Must be greater than 0')
    .optional(),
  contextMetricUnit: z.nativeEnum(ContextMetricUnitEnum).optional(),
}).superRefine((data, ctx) => {
  if (data.contextMetric && data.contextMetric !== NO_METRIC_OPTION) {
    // If a metric is chosen value becomes required.
    if (data.contextMetricValue == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a value for the metric',
        path: ['contextMetricValue'],
      });
    }
    // If a money metric is chosen unit becomes required.
    if (metricRequiresUnit(data.contextMetric) && !data.contextMetricUnit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select a unit for the metric',
        path: ['contextMetricUnit'],
      });
    }
  }
});

interface CreateReportLayoutProps {
  reportType: OperationReportTypeEnum;
  onClosePath: string;
}

export type CreateReportFormValues = z.infer<typeof schema>;

export type CreateReportContext = {
  reportType?: OperationReportTypeEnum;
  isEditing?: boolean;
  isSaving?: boolean;
  onSubmit: (data: CreateReportFormValues) => Promise<void | (() => void)>;
};

export default function CreateReportLayout({
  reportType,
  onClosePath,
}: CreateReportLayoutProps) {
  const { report_uuid: reportUuid } = useParams();
  const isEditing = Boolean(reportUuid);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { mutateAsync: createReport, isLoading: isCreating } =
    useCreateOperationReport();

  const { mutateAsync: updateReport, isLoading: isUpdating } =
    useUpdateOperationReport();

  const {
    data: reportData,
    isLoading: isLoadingReport,
    status: reportLoadingStatus,
  } = useGetOperationReport(reportUuid!);

  const existingFilters = useSortReportFilters(
    reportData ? (reportData.filters as unknown as ReportFilters[]) : []
  );

  const form = useForm<CreateReportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      contextMetric: NO_METRIC_OPTION,
      contextMetricUnit: ContextMetricUnitEnum.GBP,
    },
  });

  useEffect(() => {
    if (reportData && !isLoadingReport && reportUuid) {
      const {
        title,
        contextMetric,
        contextMetricValue,
        contextMetricUnit,
        includesFood,
        includesPackaging,
      } = reportData;

      const goodsTypes =
        includesFood && includesPackaging
          ? []
          : [
              ...(includesFood ? [OperationReportGoodsTypeEnum.food] : []),
              ...(includesPackaging
                ? [OperationReportGoodsTypeEnum.packaging]
                : []),
            ];

      form.reset({
        title: title || '',
        timePeriods: existingFilters.timePeriods.map((tp) => tp.uuid),
        goodsTypes,
        sites: existingFilters.sites.map((site) => site.uuid),
        tags: existingFilters.tags.map((tag) => tag.uuid),
        customers: existingFilters.customers.map((customer) => customer.uuid),
        contextMetric: contextMetric || NO_METRIC_OPTION,
        contextMetricValue: contextMetricValue || undefined,
        contextMetricUnit: contextMetricUnit || ContextMetricUnitEnum.GBP,
      });
    }
  }, [reportData, isLoadingReport, form, reportUuid, existingFilters]);

  useEffect(() => {
    if (!user?.organizationReportingStartingDate) {
      navigate(generatePath(ccfRoutes.REPORTING_PERIOD));
    }
  }, [navigate, user?.organizationReportingStartingDate]);

  const onSubmit = async (formData: z.infer<typeof schema>) => {
    const { contextMetric, contextMetricValue, contextMetricUnit, ...rest } =
      formData;
    const data = {
      type: reportType,
      ...rest,
      ...(contextMetric === NO_METRIC_OPTION
        ? {}
        : {
            contextMetric,
            contextMetricUnit: metricRequiresUnit(contextMetric)
              ? contextMetricUnit
              : undefined,
            contextMetricValue,
          }),
    };

    try {
      const res = reportUuid
        ? await updateReport({ operationReportUuid: reportUuid, data })
        : await createReport({ data });
      navigate(
        generatePath(getReportsPathMap()[reportType].publish!, {
          report_uuid: res.uuid,
        }),
        { replace: true }
      );
    } catch (error) {
      sentry.log(error);
      toast({
        title: `Error ${isEditing ? 'updating' : 'creating'} report`,
        description: 'Please try again or get in touch',
        variant: 'destructive',
      });
      return;
    }
  };

  const context: CreateReportContext = {
    reportType,
    isEditing,
    isSaving: isCreating || isUpdating,
    onSubmit,
  };

  return (
    <Page
      name={`${isEditing ? 'Edit' : 'New'} ${reportType} report`}
      page="guide"
      status={isEditing ? reportLoadingStatus : 'success'}
    >
      <div
        className={
          'flex h-full min-h-screen flex-col bg-gradient-to-r from-teal-300 to-teal-100'
        }
      >
        <Navbar
          logo={{
            variant: 'name',
            color: 'dark',
          }}
          includeCloseButton
          closeButtonProps={{
            onClick: () => navigate(onClosePath),
          }}
        />
        <div className="grid flex-1 grid-cols-1 overflow-y-auto p-2">
          <Form {...form}>
            <Outlet context={context} />
          </Form>
        </div>
      </div>
    </Page>
  );
}
